import * as turf from '@turf/turf';
import * as d3 from 'd3';
import _ from 'lodash';
import supercluster from 'supercluster';
import tinycolor from 'tinycolor2';
import { HeatLayer } from './HeatLayer';
import { LegendLayer } from './LegendLayer';
import { TagsLayer } from './TagLayer';
import VectorLayer from './VectorLayer';

// import 'mapbox.js';

export class D3Layer extends VectorLayer {
    constructor(options) {
        super(options);

        this._calculateProjection = this._calculateProjection.bind(this);
        this._pathProjection = this._pathProjection.bind(this);
        this._pointProjection = this._pointProjection.bind(this);
        this._setClusterStyle = this._setClusterStyle.bind(this);
        this._getClusterDataSource = this._getClusterDataSource.bind(this);
        this._paintFeatures = this._paintFeatures.bind(this);
    }

    eachLayer(callback) {
        this.layerGroup.selectAll('path').filter(f => callback(f));
    }

    show() {
        this._makeRequest();
    }

    getLayer(id) {
        if (this.layerGroup) {
            return this.layerGroup.selectAll('path').filter(f => {
                if (f) {
                    if (!_.isArray(this.options.uniqueField)) {
                        if (String(this._getFeatureId(f)) === String(id)) {
                            return true;
                        }
                    } else {
                        if (
                            _(this.options.uniqueField)
                                .filter(p => String(f.properties[p]) === String(id))
                                .size()
                        ) {
                            return true;
                        }
                    }
                }
            });
        }
    }

    _fireMovementEvent(movement) {
        if (this._checkLayerZoomVisibility()) {
            if (!this.getMap().hasLayer(this.layer)) {
                this._showVectors();
            }

            const type = this.getType();

            if (this.options.showAll) {
                if (!this._gotAll) {
                    this._gotAll = true;
                    this._makeRequest();
                } else if (_.some(['voronoi', 'heat'], t => t === type)) {
                    this._processFeatures(this.toGeoJSON());
                } else if ((this.options.filter || {}).location) {
                    this._processFeatures(null, false);
                } else {
                    this._setFeaturesPosition();
                }
            } else if (!this._checkSamePosition(movement) || this.state.features.size === 0) {
                this._makeRequest();
            } else if (_.some(['voronoi', 'heat'], t => t === type)) {
                this._processFeatures(this.toGeoJSON());
            } else if ((this.options.filter || {}).location) {
                this._processFeatures(null, false);
            } else {
                this._setFeaturesPosition();
            }
            return true;
        }

        return false;
    }

    _createLayer() {
        this._hideVectors();

        const layer = L.featureGroup();
        this.getMap().addLayer(layer);

        if (this.checkType('heat')) {
            return layer;
        }

        // Put an SVG element into Leaflet's .leaflet-overlay-pane
        const svgLayer = L.svg({ clickable: true });
        layer.addLayer(svgLayer);

        const d3Layer = d3.select(svgLayer._container);

        // Put a group element within that
        this.layerGroup = d3Layer.append('g');
        this.layerGroup.attr('id', this.id);
        this.layerGroup.attr('class', 'leaflet-d3-group');

        return layer;
    }

    getLayers(type) {
        return this.layerGroup.selectAll(type || this.getType());
    }

    _setFeaturesPosition(layers) {
        const type = this.getType();
        if (!layers) {
            if (!this.layer || !this.layerGroup || _.some(['voronoi', 'heat'], t => t === type)) {
                return;
            }
        }

        switch (type) {
        case 'voronoi':
            return layers.attr('d', d => (d ? 'M' + d.join('L') + 'Z' : null));

        case 'cluster':
            try {
                layers = this.getLayers('image').data(this._getClusterDataSource);
                layers.exit().remove();
                layers
                    .enter()
                    .append('image')
                    .merge(layers)
                    .each(this._setClusterStyle)
                    .style('cursor', 'pointer')
                    .attr('pointer-events', 'auto')
                    .attr('xlink:href', (f, i) => this.styles[i].icon && this.styles[i].icon.iconUrl)
                    .attr('width', (f, i) => this.styles[i].icon && this.styles[i].icon.iconSize[0])
                    .attr('height', (f, i) => this.styles[i].icon && this.styles[i].icon.iconSize[1])
                    .attr('x', (feature, index) => (this._pointProjection(feature.geometry.coordinates).x - (((this.styles[index] || {}).icon || { iconAnchor: [] }).iconAnchor[0] || 0)))
                    .attr('y', (feature, index) => (this._pointProjection(feature.geometry.coordinates).y - (((this.styles[index] || {}).icon || { iconAnchor: [] }).iconAnchor[1] || 0)));

                layers = this.getLayers('image');

                if ((this.options.popup || {}).clusterTemplate) {
                    if (this.options.popup.trigger === 'mouseover') {
                        this._addEventHandle(layers, 'mouseover', feature => {
                            this.setClusterPopup(feature);
                            this.createPolygonFromCluster(feature);
                        });
                    } else {
                        this._addEventHandle(layers, { trigger: this.options.popup.trigger || 'click' }, this.setClusterPopup);
                        this._addEventHandle(layers, 'mouseover', this.createPolygonFromCluster);
                    }
                } else {
                    this._addEventHandle(layers, 'mouseover', this.createPolygonFromCluster);
                }

                return layers;
            } catch (err) {
                console.log(err);
            }
            break;

        case 'image':
            return (layers || this.getLayers(type))
                .attr('x', (feature, index) =>
                    (this._pointProjection(feature.geometry.coordinates).x - ((this.styles[index].icon || {}).iconAnchor[0] || 0)))
                .attr('y', (feature, index) =>
                    (this._pointProjection(feature.geometry.coordinates).y - ((this.styles[index].icon || {}).iconAnchor[1] || 0)));

        case 'circle':
            return (layers || this.getLayers(type))
                .attr('cx', feature => this._pointProjection(feature.geometry.coordinates).x)
                .attr('cy', feature => this._pointProjection(feature.geometry.coordinates).y);

        default:
            return (layers || this.getLayers(type)).attr('d', this._pathProjection());
        }
    }

    _paintFeatures(layers = this.getLayers()) {
        const manualPaints = this.options.manualPaints || new Map();

        return layers
            .attr('fill', (f, i) => {
                const props = (f && f.properties) || {};
                const id = props.Id || props.Geocodigo;

                return manualPaints.get(id)
                    || props._fillColor
                    || (this.styles[i] || this.styles[0]).fillColor
                    || 'blue';
            })
            .attr('fill-opacity', (f, i) => (this.styles[i] || this.styles[0]).fillOpacity || 0.6)
            .attr('stroke', (f, i) => (this.styles[i] || this.styles[0]).color || 'blue')
            .attr('stroke-linejoin', (f, i) => (this.styles[i] || this.styles[0]).lineJoin || 'round')
            .attr('stroke-opacity', (f, i) => (this.styles[i] || this.styles[0]).opacity || 1)
            .attr('stroke-width', (f, i) => (this.styles[i] || this.styles[0]).weight || 1);
    }

    _resetLayerStyle() {
        if (this.layer) {
            this._paintFeatures();
        }

        this.state.highlight = new Map();
    }

    _changeLayerStyle(feature, layer, options) {
        if (this.state.highlight.has(this._getFeatureId(feature)) || options.force) {
            if (tinycolor(options.color).isValid() && layer && layer.attr) {
                return layer.attr('stroke', options.color).attr('stroke-width', options.weight);
            }
        }
    }

    // Get the appropriate Leaflet vector options for all feature
    _createStyle(featureCollection) {
        // console.time "L.VectorLayer.D3 -> _createStyle"
        if (!this.options.symbology || !featureCollection) {
            return [];
        }

        this._colorRange = this._createSymbologyScale(featureCollection);

        // console.timeEnd "L.VectorLayer.D3 -> _createStyle"
        return featureCollection.features
            ? _.map(featureCollection.features, f => this._getStyle(f))
            : this._getStyle(featureCollection);
    }

    _highlightLayer(layers = this.getLayers()) {
        this._addEventHandle(layers, { trigger: 'mouseover' }, (feature, l, i) => {
            return this.highlight(feature, { layer: l, style: this.styles[i] });
        });
        this._addEventHandle(layers, { trigger: 'mouseout' }, (feature, l, i) => {
            return this.unhighlight(feature, { layer: l, style: this.styles[i] });
        });

        layers.each((f, i) => {
            if (this.state.highlight.get(this._getFeatureId(f))) {
                this.highlight(f, {
                    style: this.styles[i],
                    layer: layers[i],
                    fixed: true,
                    force: true
                });
            }
        });
    }

    _generateVoronoi(featureCollection) {
        const points = _.map(featureCollection.features, f => {
            const latlng = this._pointProjection(f.geometry.coordinates);
            return [latlng.x, latlng.y];
        });
        const bbox = turf.bboxPolygon(
            this.getMap()
                .getBounds()
                .pad(0.025)
                .toBBoxString()
                .split(',')
        );
        const clipper = _.map(bbox.geometry.coordinates[0], p => this._calculateProjection(p));
        const voronoi = d3.voronoi().extent([clipper[3], clipper[1]]);
        const shape = voronoi.polygons(points);

        const self = this;
        this.layer.toGeoJSON = () => {
            const polygon = _.map(shape, pts =>
                _(pts)
                    .filter(pt => pt)
                    .map(pt => self.getMap().layerPointToLatLng(pt))
                    .value()
            );
            return L.polygon(polygon).toGeoJSON();
        };

        return shape;
    }

    _generateCluster(featureCollection) {
        const cluster = supercluster({
            radius: this.options.controlRange,
            maxZoom: 16
        });
        cluster.load(featureCollection.features);

        return cluster;
    }

    _getClusterDataSource() {
        const getSouthWest = this.getMap()
            .getBounds()
            .getSouthWest();
        const getNorthEast = this.getMap()
            .getBounds()
            .getNorthEast();

        return this.layer.clusterGroup.getClusters(
            [getSouthWest.lng, getSouthWest.lat, getNorthEast.lng, getNorthEast.lat],
            Math.round(this.getMap().getZoom())
        );
    }

    _setClusterStyle(f, i) {
        let iconStyle;
        if (f.properties.cluster) {
            const icon = this.options.symbology.cluster.template({
                getChildCount() {
                    return f.properties.point_count;
                },
                getAllChildMarkers: () => {
                    return this.layer.clusterGroup.getLeaves(f.properties.cluster_id, Infinity).map(f => ({
                        feature: f
                    }));
                }
            });
            iconStyle = {
                icon: {
                    iconUrl: icon.options.iconUrl,
                    iconSize: [icon.options.iconSize.x, icon.options.iconSize.y],
                    iconAnchor: icon.options.iconAnchor
                        ? [icon.options.iconAnchor.x, icon.options.iconAnchor.y]
                        : [0, 0]
                }
            };
        } else {
            const style = this._getStyle(f);
            if (style.icon instanceof L.Icon) {
                iconStyle = {
                    icon: {
                        iconUrl: style.icon.options.iconUrl,
                        iconSize: [style.icon.options.iconSize.x, style.icon.options.iconSize.y],
                        iconAnchor: [style.icon.options.iconAnchor.x, style.icon.options.iconAnchor.y]
                    }
                };
            } else {
                iconStyle = style;
            }
        }

        this.styles[i] = iconStyle;
    }

    createPolygonFromCluster(feature) {
        if (this.options.noAllowClusterHull) {
            return;
        };

        if (feature.properties.cluster) {
            const features = turf.convex(
                turf.featureCollection(this.layer.clusterGroup.getLeaves(feature.properties.cluster_id, Infinity))
            );

            if (!features) {
                return;
            }

            const coordinates = features.geometry.coordinates[0].map(f => [f[1], f[0]]);
            this.clusterPolygon = L.polygon(coordinates, {
                color: '#e74c3c',
                fillColor: '#e74c3c',
                fillOpacity: 0.5,
                strokeWeight: 2,
                opacity: 1,
                weight: 1
            }).addTo(this.getMap());

            this.getLayers('image').on('mouseout', this._removeClusterPolygon);
        }
    }

    setClusterPopup(feature) {
        const marker = L.marker(L.latLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]));

        if (feature.properties.cluster) {
            const cluster = this.layer.clusterGroup
                .getLeaves(feature.properties.cluster_id, Infinity)
                .map(c => ({ feature: c }));

            this._showPopup(cluster, marker);
        } else {
            this._showPopup(feature, marker);
        }
    }

    getType() {
        if (this.options.voronoi) {
            return 'voronoi';
        } else if (this.options.type === 'cluster') {
            return 'cluster';
        } else if (this.options.type === 'decluster') {
            return 'image';
        } else if (this.options.type === 'heat') {
            return 'heat';
        } else if (this.options.symbology && (this.options.symbology.style || {}).icon) {
            return 'image';
        } else if (this.options.symbology && (this.options.symbology.style || {}).circleMaker) {
            return 'circle';
        } else {
            return 'path';
        }
    }

    _createFeaturesLayer(featureCollection) {
        // console.time('L.VectorLayer.D3 -> _createFeaturesLayer');
        const type = this.getType();
        let layers;

        switch (type) {
        case 'voronoi':
            layers = this.getLayers('path')
                .data(this._generateVoronoi(featureCollection))
                .enter()
                .append('path');
            break;

        case 'cluster':
            if (this.layer) {
                this.layer.clusterGroup = this._generateCluster(featureCollection);
            }
            layers = this.getLayers('image');
            break;

        case 'image':
            layers = this.getLayers(type).data(featureCollection.features);
            layers.exit().remove();
            layers
                .enter()
                .append(type)
                .merge(layers)
                .style('cursor', 'pointer')
                .attr('pointer-events', 'auto')
                .attr('xlink:href', (f, i) => this.styles[i].icon.iconUrl)
                .attr('width', (f, i) => this.styles[i].icon.iconSize[0])
                .attr('height', (f, i) => this.styles[i].icon.iconSize[1]);

            layers = this.getLayers(type);
            break;

        case 'circle':
            layers = this.getLayers(type).data(featureCollection.features);
            layers.exit().remove();
            layers
                .enter()
                .append(type)
                .merge(layers)
                .style('cursor', 'pointer')
                .attr('r', this.options.symbology.style.radius || 2);

            layers = this.getLayers(type);
            break;

        default:
            layers = this.getLayers(type).data(featureCollection.features);
            layers.exit().remove();
            layers
                .enter()
                .append(type)
                .merge(layers)
                .style('cursor', 'pointer');

            layers = this.getLayers(type);
        }

        this._setFeaturesPosition(layers);

        if (this.options.symbology && !_.some(['cluster', 'image', 'heat'], t => this.checkType(t))) {
            this._paintFeatures(layers);
            if ((this.options.symbology.style || {}).highlight) {
                this._highlightLayer(layers);
            }
        }

        // console.timeEnd('L.VectorLayer.D3 -> _createFeaturesLayer');
    }

    _addEventHandle(layers, event, callback) {
        const runCallback = (f, i) => callback.call(this, f, layers[i] || this.layer, i);
        if (event.trigger) {
            layers.on(event.trigger, runCallback);
        } else {
            layers.on(event, runCallback);
        }
    }

    async _onEachFeature(featureCollection) {
        // console.time('L.VectorLayer.D3 -> _onEachFeature');

        if (this.checkType('heat')) {
            this.layer.addLayer(
                HeatLayer.geoJsonToHeatLayer(featureCollection, { ...this.options, map: this.getMap() })
            );
            return;
        }

        this.styles = this._createStyle(featureCollection);
        this._createFeaturesLayer(featureCollection);

        const layers = this.getLayers();
        layers.attr('id', (f, i) => (f && f.properties ? f.properties[this.options.uniqueField] : i));

        this._addEventHandle(layers, 'click', this._fireClickEvent);
        this._addEventHandle(layers, 'dblclick', this._fireDoubleClickEvent);
        this._addEventHandle(layers, 'mouseover', this._fireMouseoverEvent);
        this._addEventHandle(layers, 'mouseout', this._fireMouseoutEvent);

        if ((this.options.popup || {}).template && !this.checkType('voronoi')) {
            this._addEventHandle(layers, { trigger: this.options.popup.trigger || 'click' }, feature => {
                this._showPopup(
                    feature,
                    L.marker(L.latLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]))
                );
            });

            if ((this.options.popup.options || {}).autoclose) {
                this._addEventHandle(layers, { trigger: 'mouseout' }, this.closePopup);
            }
        }

        // console.timeEnd('L.VectorLayer.D3 -> _onEachFeature');
    }

    _calculateProjection(latlng) {
        const p = this._pointProjection(latlng);
        return [p.x, p.y];
    }

    // Use Leaflet to project from geographic to pixel coordinates
    _pointProjection(latlng) {
        let location;
        try {
            location = new L.LatLng(latlng[1], latlng[0]);
        } catch (err) {
            console.log(err);
            location = new L.LatLng(0, 0);
        }

        return this.getMap().latLngToLayerPoint(location);
    }

    _pathProjection() {
        const self = this;
        const transform = d3.geoTransform({
            point: function(x, y) {
                const point = self._pointProjection([x, y]);
                this.stream.point(point.x, point.y);
            }
        });

        return d3.geoPath().projection(transform);
    }

    // _getLayerPosition () {
    //     const getSouthWest = this.getMap().getBounds().pad(0.1).getSouthWest();
    //     const getNorthEast = this.getMap().getBounds().pad(0.1).getNorthEast();
    //
    //     return {
    //         sw: this._pointProjection([getSouthWest.lng, getSouthWest.lat]),
    //         ne: this._pointProjection([getNorthEast.lng, getNorthEast.lat])
    //     };
    // }

    _processFeatures(res, rebuild = true) {
        // console.time('L.VectorLayer.D3 -> _processFeature');
        if (this.loadingLayer) {
            clearTimeout(this.loadingLayer);
        }

        if (rebuild) {
            this.setFeatures(res);
            this.loadingLayer = setTimeout(async () => {
                this.layer = this._createLayer();
                this.layer.id = this.id;
                this.state.visible = true;
                await this._startProcessing();
                delete this.loadingLayer;
            }, 100);
        } else {
            return this._startProcessing(true);
        }
        // console.timeEnd('L.VectorLayer.D3 -> _processFeature');
    }

    async _startProcessing (fastMode = false) {
        const featureCollection = await this.getCurrentGeoJSON(fastMode);

        if (!this.getMap() || !_.size(featureCollection.features)) {
            return;
        }

        this._onEachFeature(featureCollection);

        if (!this.checkType('heat')) {
            if (this.options.tag) {
                this._removeTags();

                let tagFeatures = featureCollection.features;
                if (this.checkType('cluster') && this.layer && this.layer.clusterGroup) {
                    tagFeatures = this._getClusterDataSource().filter(f => !(f.properties || {}).cluster);
                }

                this._tags = new TagsLayer({
                    features: tagFeatures,
                    getStyle: this._getStyle,
                    layer: L.geoJSON({ type: 'FeatureCollection', features: tagFeatures }),
                    map: this.getMap(),
                    options: this.options
                });
            }

            if (this.options.legend) {
                this._removeLegend();
                this._legend = new LegendLayer({
                    colorRange: this._colorRange,
                    features: featureCollection.features,
                    getColorRange: this._getColorRange,
                    map: this.getMap(),
                    options: this.options
                });
            }

            try {
              if (document.getElementById(this.id)){
                const el = document.getElementById(this.id).parentElement;
                if (el && this.options.above) {
                  el.parentNode.appendChild(el);
                } else if (el && this.options.below) {
                  const firstChild = el.parentNode.firstChild;
                  if (firstChild) {
                    el.parentNode.insertBefore(el, firstChild);
                  }
                }
              }
            } catch (err) {
                console.log(err);
            }
        }

        if (this.options.done) {
            this.options.done(featureCollection.features);
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/D3Layer.js